import { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCode, faCheck } from '@fortawesome/pro-regular-svg-icons';

import { CssEditor } from './CssEditor';
import rootElement from '../../rootElement';

import './CssEditorModal.scss';

interface CssEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onChange: (value: string) => void;
}

const DEFAULT_CSS_TEMPLATE = `:host {
  /* Custom styles for the interactive map container */

}

/* Example: Style markers */
/*
.leaflet-marker-icon {

}
*/

/* Example: Style the sidebar */
/*
.sidebar {

}
*/
`;

export function CssEditorModal({ isOpen, onClose, value, onChange }: CssEditorModalProps) {
  // Use template when value is empty
  const initialValue = value || DEFAULT_CSS_TEMPLATE;
  const [localValue, setLocalValue] = useState(initialValue);

  // Sync local value when modal opens or external value changes
  useEffect(() => {
    if (isOpen) {
      setLocalValue(value || DEFAULT_CSS_TEMPLATE);
    }
  }, [isOpen, value]);

  const handleSave = useCallback(() => {
    // Don't save the template if user hasn't modified it
    const valueToSave = localValue === DEFAULT_CSS_TEMPLATE ? '' : localValue;
    onChange(valueToSave);
    onClose();
  }, [localValue, onChange, onClose]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
    if (e.key === 's' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSave();
    }
  }, [onClose, handleSave]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="css-editor-modal" onClick={onClose}>
      <div className="css-editor-modal__container" onClick={(e) => e.stopPropagation()}>
        <header className="css-editor-modal__header">
          <div className="css-editor-modal__title">
            <FontAwesomeIcon icon={faCode} />
            <h2>Custom CSS Editor</h2>
          </div>
          <div className="css-editor-modal__actions">
            <button
              className="css-editor-modal__save"
              onClick={handleSave}
              title="Save (Ctrl+S)"
            >
              <FontAwesomeIcon icon={faCheck} />
              <span>Save</span>
            </button>
            <button
              className="css-editor-modal__close"
              onClick={onClose}
              aria-label="Close editor"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </header>

        <div className="css-editor-modal__help">
          <p>
            Add custom CSS to override default styles. Use <code>:host</code> to target the shadow root container.
            Press <kbd>Ctrl+S</kbd> to save, <kbd>Esc</kbd> to close.
          </p>
        </div>

        <div className="css-editor-modal__editor">
          <CssEditor
            value={localValue}
            onChange={setLocalValue}
            height="100%"
          />
        </div>
      </div>
    </div>
  );

  // Render into the root element (shadow root or regular DOM)
  return createPortal(modalContent, rootElement);
}

export default CssEditorModal;
